import React, { Fragment, useState } from 'react';
import TableBuilder, { ButtonBuilder, HoverOver, CommonModal } from '../../../../../../../../GlobalComponents/Builders';
import { buildSelectOptionsFromArray, handleTabOnKeyDown } from '../../../../../../../../GlobalComponents/Helpers';
import { X, Trash2 } from 'react-feather';
import DatePicker from 'react-datepicker';
import Select from 'react-select';

const PHASE_DEFAULT_ROW = { phase: '1', data_import: '', status: 'pending', record_count: '', due_date: '', filters: '', custom_logic: '', input_required: '', notes: '' };

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'complete', label: 'Complete' },
    { value: 'blocked', label: 'Blocked' },
];

const inputStyle = { height: 34, fontSize: 13, width: '100%' };

const PhasesSection = ({
    sections,
    setSections,
    isEditing,
    sourceResources = [],
    sourceConnectionId,
    assessmentId,
}) => {
    const [removeConfirm, setRemoveConfirm] = useState(null); // { index, row }

    const getPhasesData = () => {
        const rows = sections.phases?.rows || [PHASE_DEFAULT_ROW];
        if (isEditing) return rows;
        return [...rows].sort((a, b) => parseFloat(a.phase || 0) - parseFloat(b.phase || 0));
    };

    const addPhaseRow = () => {
        const rows = [...getPhasesData()];
        const nextPhase = rows.length > 0 ? String(parseFloat(rows[rows.length - 1].phase || rows.length) + 1) : '1';
        rows.push({ ...PHASE_DEFAULT_ROW, phase: nextPhase });
        setSections((prev) => ({ ...prev, phases: { rows } }));
    };

    const updatePhaseRow = (index, field, value) => {
        const rows = [...getPhasesData()];
        rows[index] = { ...rows[index], [field]: value };
        setSections((prev) => ({ ...prev, phases: { rows } }));
    };

    const requestRemovePhaseRow = (index) => {
        const row = getPhasesData()[index];
        const hasData = row.data_import || row.record_count || row.notes;
        if (hasData) {
            setRemoveConfirm({ index, row });
        } else {
            removePhaseRow(index);
        }
    };

    const removePhaseRow = (index) => {
        const rows = getPhasesData().filter((_, i) => i !== index);
        setSections((prev) => ({ ...prev, phases: { rows } }));
        setRemoveConfirm(null);
    };

    const resourceOptions = buildSelectOptionsFromArray(sourceResources, { label: 'name', value: 'name' });
    const phasesData = getPhasesData();

    const hasRecordCount = (row) => row.record_count && row.record_count !== '';

    return (
        <Fragment>
            {isEditing && (
                <div className='mb-3'>
                    <ButtonBuilder label='Add Phase' className='btn-outline-primary btn-sm' onClick={addPhaseRow} />
                </div>
            )}
            <TableBuilder
                data={phasesData}
                processing={false}
                pagination={false}
                title=''
                styles={{ cells: { style: { alignItems: 'flex-start', paddingTop: 10, paddingBottom: 10 } } }}
                columns={[
                    {
                        name: 'Phase',
                        cell: (row, i) => isEditing
                            ? <input className='form-control' value={row.phase}
                                onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ''); updatePhaseRow(i, 'phase', v); }}
                                style={inputStyle} />
                            : row.phase,
                        center: true, width: '6%',
                    },
                    {
                        name: 'Resource',
                        cell: (row, i) => isEditing
                            ? <div className='dmt-phases-select' style={{ width: '100%' }}>
                                <Select
                                    className='react-select-container'
                                    classNamePrefix='react-select'
                                    options={resourceOptions}
                                    value={resourceOptions.find(o => o.value === row.data_import) || null}
                                    onChange={(opt) => updatePhaseRow(i, 'data_import', opt?.value || '')}
                                    placeholder='Select resource...'
                                    isClearable
                                    menuPortalTarget={document.body}
                                    menuPosition='fixed'
                                />
                              </div>
                            : row.data_import || '-',
                        center: true, width: '20%',
                    },
                    {
                        name: 'Status',
                        cell: (row, i) => isEditing
                            ? <div className='dmt-phases-select' style={{ width: '100%' }}>
                                <Select
                                    className='react-select-container'
                                    classNamePrefix='react-select'
                                    options={STATUS_OPTIONS}
                                    value={STATUS_OPTIONS.find(o => o.value === row.status) || null}
                                    onChange={(opt) => updatePhaseRow(i, 'status', opt?.value || 'pending')}
                                    menuPortalTarget={document.body}
                                    menuPosition='fixed'
                                />
                              </div>
                            : (STATUS_OPTIONS.find(o => o.value === row.status)?.label || row.status),
                        center: true, width: '10%',
                    },
                    {
                        name: 'Due Date',
                        cell: (row, i) => isEditing
                            ? <DatePicker
                                selected={row.due_date ? new Date(row.due_date) : null}
                                onChange={(date) => updatePhaseRow(i, 'due_date', date ? date.toISOString().split('T')[0] : '')}
                                dateFormat='MMM d, yyyy'
                                placeholderText='Select date...'
                                className='form-control'
                                isClearable
                                portalId='root'
                                wrapperClassName='w-100'
                              />
                            : row.due_date ? new Date(row.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-',
                        center: true, width: '10%',
                    },
                    {
                        name: '# Records',
                        cell: (row, i) => isEditing
                            ? <input className='form-control' value={row.record_count} onChange={(e) => updatePhaseRow(i, 'record_count', e.target.value)} style={inputStyle} placeholder={sourceConnectionId ? 'Use Record Counts ↓' : ''} />
                            : hasRecordCount(row) ? Number(row.record_count).toLocaleString() : '-',
                        center: true, width: '9%',
                    },
                    {
                        name: 'Notes',
                        cell: (row, i) => isEditing
                            ? <textarea className='form-control' value={row.notes} onChange={(e) => updatePhaseRow(i, 'notes', e.target.value)} onKeyDown={handleTabOnKeyDown} style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} />
                            : row.notes ? <span style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{row.notes}</span> : '-',
                        center: true, grow: 1,
                    },
                    ...(isEditing ? [{
                        name: '',
                        cell: (row, i) => <Trash2 size={15} style={{ cursor: 'pointer', color: '#dc3545' }} onClick={() => requestRemovePhaseRow(i)} />,
                        center: true, width: '50px',
                    }] : []),
                ]}
            />

            <CommonModal
                isOpen={!!removeConfirm}
                toggler={() => setRemoveConfirm(null)}
                title='Remove Phase'
                onSubmit={false}
            >
                <p>Remove <strong>Phase {removeConfirm?.row?.phase}</strong>{removeConfirm?.row?.data_import ? ` (${removeConfirm.row.data_import})` : ''}?</p>
                {removeConfirm?.row?.record_count && (
                    <p className='text-muted' style={{ fontSize: 13 }}>This phase has a record count of {Number(removeConfirm.row.record_count).toLocaleString()}. Any associated filter configurations will also be lost.</p>
                )}
                <div className='d-flex gap-2 justify-content-end mt-3'>
                    <button className='btn btn-sm btn-outline-secondary' onClick={() => setRemoveConfirm(null)}>Cancel</button>
                    <button className='btn btn-sm btn-danger' onClick={() => removePhaseRow(removeConfirm.index)}>Remove Phase</button>
                </div>
            </CommonModal>
        </Fragment>
    );
};

export default PhasesSection;
